import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Chip, LinearProgress, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { designTokens } from '../../theme/designTokens';

interface Learner {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    preferences?: {
        photoUrl?: string;
    };
}

interface Progress {
    userId: string;
    completedLessons: number;
    totalLessons: number;
    progressPercentage: number;
}

const TrainerApprenantsPage = () => {
    const { user } = useAuth();
    const [learners, setLearners] = useState<Learner[]>([]);
    const [progressData, setProgressData] = useState<{ [key: string]: Progress }>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAssignedLearners();
    }, []);

    const fetchAssignedLearners = async () => {
        try {
            // Fetch current user to get assigned learners
            const userRes = await api.get(`/api/users/${user?._id}`);
            const assignedLearnerIds = userRes.data.trainerSettings?.assignedLearners || [];

            if (assignedLearnerIds.length === 0) {
                setLoading(false);
                return;
            }

            // Fetch learner details
            const learnersRes = await api.get('/api/users');
            const assignedLearners = learnersRes.data.filter((u: Learner) =>
                assignedLearnerIds.includes(u._id) && u
            );
            setLearners(assignedLearners);

            // Fetch progress for each learner
            const progressPromises = assignedLearners.map((learner: Learner) =>
                api.get(`/api/progress/user/${learner._id}`).catch(() => ({ data: [] }))
            );
            const progressResults = await Promise.all(progressPromises);

            const progressMap: { [key: string]: Progress } = {};
            assignedLearners.forEach((learner: Learner, index: number) => {
                const userProgress = progressResults[index].data;
                let totalCompleted = 0;
                let totalLessons = 0;

                userProgress.forEach((courseProgress: any) => {
                    totalCompleted += courseProgress.completedLessons?.length || 0;
                    totalLessons += courseProgress.totalLessons || 0;
                });

                progressMap[learner._id] = {
                    userId: learner._id,
                    completedLessons: totalCompleted,
                    totalLessons: totalLessons,
                    progressPercentage: totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0
                };
            });

            setProgressData(progressMap);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching assigned learners", err);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (learners.length === 0) {
        return (
            <Box sx={{ p: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center', ...designTokens.glassCard }}>
                    <Typography variant="h6" color="text.secondary">
                        Aucun apprenant ne vous est assigné pour le moment.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom>Mes Apprenants</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Liste des apprenants qui vous sont assignés
            </Typography>

            <TableContainer component={Paper} sx={{ ...designTokens.glassCard }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Apprenant</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Progression</TableCell>
                            <TableCell>Leçons complétées</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {learners.map((learner) => {
                            const progress = progressData[learner._id] || { completedLessons: 0, totalLessons: 0, progressPercentage: 0 };
                            return (
                                <TableRow key={learner._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar src={learner.preferences?.photoUrl}>
                                                {learner.firstName[0]}{learner.lastName[0]}
                                            </Avatar>
                                            <Typography>
                                                {learner.firstName} {learner.lastName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{learner.email}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progress.progressPercentage}
                                                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {progress.progressPercentage}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${progress.completedLessons} / ${progress.totalLessons}`}
                                            size="small"
                                            color={progress.progressPercentage === 100 ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TrainerApprenantsPage;
